/*  iceberg-ui  */
import { IconWithVariant } from '@auviknetworks/iceberg-ui';

/*  MUI  */
import {
    Accordion,
    AccordionDetails,
    AccordionSummary,
    Box,
    Button,
    Checkbox,
    FormControlLabel,
    Grid,
    Typography
} from '@mui/material';

/*  Shared  */
import { FontSizes } from 'application/Shared/Constants';

/*  Helpers */
import { Notify } from 'application/Shared/Helpers';
import { debug } from 'application/Shared/Utils';

/*  NPM  */
import axios from 'axios';
import { FEATURE_FLAGS_FROM_TENANT_SETTINGS } from 'featureFlags/src';

/*  React  */
import React, { useState } from 'react';
import { useTranslation } from 'react-i18next';

const save = async (tenant, settingsFormData, onSuccessFn, t) => {
    try {
        await Promise.all(
            FEATURE_FLAGS_FROM_TENANT_SETTINGS.map(async (featureFlag) => {
                await axios.post('/tenantSetting', {
                    global: true,
                    key: featureFlag,
                    platformTenantId: tenant.id,
                    value: settingsFormData[featureFlag]
                });
            })
        );

        Notify.s(
            t('GlobalAdmin.Tenant.Edit.Actions.successfullyUpdatedFeatureFlags')
        );
        onSuccessFn();
    } catch (e) {
        Notify.e(
            t('GlobalAdmin.Tenant.Edit.Actions.unabletoUpdateFeatureFlags')
        );
        debug.e(e);
    }
};

export const TenantFeatureFlags = ({ tenant, onSuccessFn }) => {
    const { t } = useTranslation();
    const [expanded, setExpanded] = useState(true);

    const { settings: originalSettings } = tenant;

    const [settingsFormData, setSettingsFormData] = useState(
        FEATURE_FLAGS_FROM_TENANT_SETTINGS.reduce((acc, featureFlag) => {
            acc[featureFlag] = originalSettings?.[featureFlag] || false;
            return acc;
        }, {})
    );

    const { partner } = tenant;

    // Only show the feature flags if the tenant is a partner tenant and there are any feature flags to show.
    if (!partner || !FEATURE_FLAGS_FROM_TENANT_SETTINGS.length) {
        return null;
    }

    return (
        <Accordion
            expanded={expanded}
            onChange={() => setExpanded(!expanded)}
            slotProps={{ transition: { unmountOnExit: true } }}
        >
            <AccordionSummary
                expandIcon={
                    <IconWithVariant
                        data-testid="expandAccordionIconTenantFeatureFlags"
                        variant="primary"
                        src="ChevronDown"
                        size={FontSizes[2]}
                    />
                }
            >
                <Box display="flex" alignItems="center">
                    <Typography variant="h6" sx={{ fontWeight: 600 }}>
                        {t('GlobalAdmin.Tenant.FeatureFlags.title')}
                    </Typography>
                </Box>
            </AccordionSummary>
            <AccordionDetails>
                {FEATURE_FLAGS_FROM_TENANT_SETTINGS.map((featureFlag) => (
                    <Grid
                        item
                        xs={12}
                        key={featureFlag}
                        sx={{ marginBottom: 2 }}
                    >
                        <FormControlLabel
                            control={
                                <Checkbox
                                    checked={settingsFormData[featureFlag]}
                                    onChange={(e) => {
                                        setSettingsFormData({
                                            ...settingsFormData,
                                            [featureFlag]: e.target.checked
                                        });
                                    }}
                                />
                            }
                            label={
                                <Box>
                                    <Typography sx={{ fontWeight: 600 }}>
                                        {t(
                                            `GlobalAdmin.Tenant.FeatureFlag.${featureFlag}.Title`
                                        )}
                                    </Typography>
                                    <Typography>
                                        {t(
                                            `GlobalAdmin.Tenant.FeatureFlag.${featureFlag}.Description`
                                        )}
                                    </Typography>
                                </Box>
                            }
                        />
                    </Grid>
                ))}

                <Grid item xs={12}>
                    <hr />
                </Grid>

                <Button
                    data-trackid="globalAdmin_tenant_tenantFeatureFlags_submit"
                    onClick={() =>
                        save(tenant, settingsFormData, onSuccessFn, t)
                    }
                    color="primary"
                    variant="contained"
                >
                    {t('GlobalAdmin.Tenant.FeatureFlag.submitButton')}
                </Button>
            </AccordionDetails>
        </Accordion>
    );
};
